import { apolloClient } from 'lib/graphql';
import classNames from 'classnames';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import * as React from 'react';

import SkillsInput from './skills_input';

export default class CandidateSkillsInput extends SkillsInput {
  tagRef: React.RefObject<any>;

  constructor(props) {
    super(props);
    this.tagRef = React.createRef();
    this.styles = require('./candidate_skills_input.module.scss');
    this.addButtonColor = 'tuftsBlue';
  }

  seed = 0;
  generateKey() {
    return this.seed++;
  }

  sortSkills(orderedSkillsAndTagsIds) {
    /*
     * We are going to filter the skills and only update their position
     * The proposed tags can't be ordered
     */
    const { skills } = this.state;
    const orderedSkills = [];
    const orderedSkillsIds = [];
    orderedSkillsAndTagsIds.forEach(id => {
      const selectedSkill = skills.find(skill => skill.id.toString() === id.toString());
      if (selectedSkill) {
        orderedSkillsIds.push(id);
        orderedSkills.push(selectedSkill);
      }
    });

    this.setState(
      () => ({
        skills: orderedSkills,
      }),
      () => this.props.updateSkills(orderedSkills)
    );

    apolloClient
      .mutate({
        mutation: GQL_SORT_SKILLS,
        variables: { ids: orderedSkillsIds },
      })
      .then(
        result => this.handleFulfilledPromisse(result, ['candidate', 'skillsSort']),
        reason => this.handleRejectedPromisse(reason)
      );
  }

  addSkill(addMutation) {
    const tagId = this.tagRef.current.getSelectedValue();

    const temporaryId = -this.state.skills.length;
    this.setState(state => ({
      skills: state.skills.concat({
        id: temporaryId,
        tag: 'adding ...',
      }),
    }));

    addMutation({ variables: { tagId } }).then(
      result => {
        if (this.handleFulfilledPromisse(result, ['candidate', 'skillCreate'])) {
          const createdSkill = result.data.candidate.skillCreate.resource;
          this.findAndUpdateSkill(temporaryId, {
            id: createdSkill.id,
            tag: createdSkill.tag.name,
          });
        } else {
          this.findAndDeleteSkill(temporaryId);
        }
      },
      reason => {
        this.findAndDeleteSkill(temporaryId);
        this.handleRejectedPromisse(reason);
      }
    );

    this.tagRef.current.clearSelectedValue();
  }

  // get Mutations

  getAddSkillMutation() {
    return GQL_ADD_SKILL;
  }

  async loadOptions(query, callback) {
    const result = await apolloClient.query({
      query: GQL_QUERY_TAGS,
      variables: { query },
    });
    return result.data.tags.nodes.map(t => ({ label: t.name, value: t.id }));
  }

  // renders
  renderInputsExtra() {
    return null;
  }

  renderInputsHelper() {
    return (
      <p className={this.styles.skillsLeftHelper}>
        {this.props.maximumNumberOfSkills -
          this.state.skills.length -
          this.state.proposedTags.length}{' '}
        Skills left
      </p>
    );
  }

  renderSkillOrTag(skillOrTag, isProposedTag = false) {
    let skillClassName;
    if (isProposedTag) {
      skillClassName = classNames(this.styles.proposedTag, 'disabled');
    } else {
      skillClassName = classNames(this.styles.skill);
    }

    return (
      // eslint-disable-next-line react/no-unknown-property
      <li skill-id={skillOrTag.id} key={this.generateKey()} className={skillClassName}>
        {!this.props.readOnly && (
          <Mutation
            mutation={isProposedTag ? this.getDeleteProposedTagMutation() : GQL_DELETE_SKILL}
          >
            {(deleteMutation, { loading, data = null, error = null }) => (
              <a
                className={this.styles.close}
                onClick={this.handleDelete(deleteMutation)(skillOrTag.id)(isProposedTag)}
                aria-label="Delete skill"
              >
                <span className="u-visuallyHidden">Delete skill</span>
              </a>
            )}
          </Mutation>
        )}
        <div className={this.styles.skillName}>{skillOrTag.tag}</div>
        {this.renderProposedTagStatus(skillOrTag, isProposedTag)}
      </li>
    );
  }

  renderDynamicSkills(skillOrTag, isProposedTag = false) {
    let skillClassName;
    if (isProposedTag) {
      skillClassName = classNames(this.styles.proposedTag, 'disabled');
    } else {
      skillClassName = classNames(this.styles.skill);
    }

    return (
      <li
        // eslint-disable-next-line react/no-unknown-property
        skill-id={skillOrTag.id}
        key={this.generateKey()}
        className={skillClassName} // skillClassName
      >
        {!this.props.readOnly && (
          <Mutation
            mutation={isProposedTag ? this.getDeleteProposedTagMutation() : GQL_DELETE_SKILL}
          >
            {(deleteMutation, { loading, data = null, error = null }) => (
              <a
                className={this.styles.close}
                onClick={this.handleDelete(deleteMutation)(skillOrTag.id)(isProposedTag)}
                aria-label="Delete skill"
              >
                <span className="u-visuallyHidden">Delete skill</span>
              </a>
            )}
          </Mutation>
        )}
        <div className={this.styles.skillName}>{skillOrTag.tag}</div>
        {this.renderProposedTagStatus(skillOrTag, isProposedTag)}
      </li>
    );
  }

  renderProposedTagStatus(skillOrTag, isProposedTag) {
    return isProposedTag ? this.renderTagExperienceLevel(skillOrTag) : null;
  }

  renderTagExperienceLevel(tag) {
    return <span className={this.styles.proposedTagStatus}>Waiting for approval</span>;
  }
}

const GQL_QUERY_TAGS = gql`
  query ($query: String) {
    tags(query: $query, excludeUserTags: true) {
      nodes {
        id
        name
      }
    }
  }
`;

export const GQL_ADD_SKILL = gql`
  mutation ($tagId: ID!) {
    candidate {
      skillCreate(tagId: $tagId) {
        errors {
          field
          message
        }
        resource {
          id
          tag {
            name
          }
        }
      }
    }
  }
`;

const GQL_DELETE_SKILL = gql`
  mutation ($id: ID!) {
    candidate {
      skillDelete(id: $id) {
        errors {
          field
          message
        }
      }
    }
  }
`;

const GQL_SORT_SKILLS = gql`
  mutation ($ids: [ID!]!) {
    candidate {
      skillsSort(ids: $ids) {
        errors {
          field
          message
        }
      }
    }
  }
`;
